import { useState } from 'react';

import { Alert, Checkbox, FormGroup, Radio, RadioGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from '../AdminAuthContainer';
import AdminSettingsContainer from '../AdminSettingsContainer';
import { H2 } from '../styles/headings';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';

const StyledFeatureContainer = styled.div`
    margin-left: 10px;
    margin-bottom: 21px;
`;

const StyledRadioGroup = styled(RadioGroup)`
    margin-left: 20px;
`;

const HelperText = styled.label`
    font-size: 14px;
    opacity: 0.85;
    cursor: pointer;
`;

const RecordingSettings = () => {
    const { adminSettings, setAdminSettings } = useContainer(AdminSettingsContainer);
    const { isAdmin } = useContainer(AdminAuthContainer);
    const { recordingSettings, featureSettings } = adminSettings;
    const { t } = useTranslation('admin_settings');
    const [radioValue, setRadioValue] = useState(recordingSettings.localSave ? 'local' : 'cloud');
    const { school } = useContainer(AdminSettingsContainer);
    const isTeams = school?.isTeams;

    const handleRecordingSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.id === 'autoSave') {
            if (event.target.checked) {
                recordingSettings.autoSave = true;
                recordingSettings.cloudSave = true;
                recordingSettings.localSave = false;
            } else {
                recordingSettings.autoSave = false;
                recordingSettings.cloudSave = false;
                recordingSettings.localSave = false;
            }
        } else if (event.target.id === 'participantsCanDownload') {
            recordingSettings.participantsCanDownload = Boolean(event.target.checked);
        }
        setAdminSettings({ ...adminSettings });
    };

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        switch (value) {
            case 'local':
                recordingSettings.cloudSave = false;
                recordingSettings.localSave = true;
                break;
            case 'cloud':
                recordingSettings.cloudSave = true;
                recordingSettings.localSave = false;
                break;
            default:
                break;
        }

        setRadioValue(value);
        setAdminSettings({ ...adminSettings });
    };

    const downloadsDisabled = Boolean(featureSettings.options.recordingsDisableDownloads);

    return (
        <>
            <H2>{t('configuration.recording-title')}</H2>

            <StyledFeatureContainer>
                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={recordingSettings.autoSave}
                                onChange={handleRecordingSettingsChange}
                                id="autoSave"
                                name="autoSave"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.record-automatic')}
                    />

                    {recordingSettings.autoSave && !isTeams && (
                        <StyledRadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            defaultValue="cloud"
                            name="radio-buttons-group"
                            value={radioValue}
                            onChange={handleRadioChange}
                        >
                            <AdminControlCheckbox
                                control={<Radio value="cloud" disabled={!isAdmin} />}
                                label={t('configuration.record-cloud')}
                            />
                            <AdminControlCheckbox
                                control={<Radio value="local" disabled={!isAdmin} />}
                                label={t('configuration.record-local')}
                            />
                            <HelperText>{t('configuration.record-local-helper')}</HelperText>
                        </StyledRadioGroup>
                    )}
                </FormGroup>

                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={Boolean(recordingSettings.participantsCanDownload)}
                                onChange={handleRecordingSettingsChange}
                                id="participantsCanDownload"
                                name="participantsCanDownload"
                                disabled={!isAdmin || downloadsDisabled}
                            />
                        }
                        label={t('user-access.recording-participants-can-download')}
                    />
                    {downloadsDisabled && (
                        <Alert severity="warning">
                            {t('configuration.recordings.downloads-disabled-cant-allow-participants')}
                        </Alert>
                    )}
                </FormGroup>
            </StyledFeatureContainer>
        </>
    );
};

export default RecordingSettings;
